import { combineLatest, forkJoin, iif as observableIif, Observable, of } from 'rxjs';
import { Component, ElementRef, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { BlujayUrlService } from '../shared/blujay-url.service';
import { LoaderService } from '../../shared/services/loader.service';
import { ActivatedRoute } from '@angular/router';
import { CarrierService } from '../shared/carrier.service';
import { DisplayMode } from '../../shared/display-mode';
import { BlujayUrl } from '../shared/blujay-url.model';
import { ToastrService } from 'ngx-toastr';
import { Carrier } from '../shared/carrier.model';
import { Location } from '@angular/common';
import { TmsType } from '../shared/tms-type.model';
import { HttpErrorResponse } from '@angular/common/http';
import { ModalService } from '../../shared/services/modal.service';
import { CarrierFormType } from '../shared/carrier-form-type';
import { ComponentCanDeactivate } from '../../core/guards/pending-changes.guard';
import { NgForm } from '@angular/forms';
import { NavigationService } from '../../shared/services/navigation.service';
import { FeatureProfile } from '../shared/feature-profile.model';
import { TranslateService } from '@ngx-translate/core';
import { BlujayService } from '../shared/blujay.service';
import { AuthenticationService } from '../../shared/services/authentication.service';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { Region } from '../shared/region.model';
import { RegionService } from '../shared/region.service';
import { ErrorHandlerService } from '../../shared/services/error-handler.service';
import { LoadRangeDisplayCma } from '../shared/load-range-display-cma.model';
import { RadioOption } from '../../shared/radio-option';
import { IDatePickerDirectiveConfig } from 'ng2-date-picker';
import * as moment from 'moment';
import { TimezoneService } from '../../shared/services/timezone.service';
import { Languages } from '../../core/languages';
import { CarrierManagerService } from '../shared/carrier-manager.service';
import { BluechatService } from '../../bluechat/bluechat.service';
import { FEATURES, FeatureService } from '../../shared/services/feature.service';
import { Option } from '../shared/feature-utils';
import { ApiFamily } from '../shared/third-party.model';

@Component({
  selector: 'app-carrier',
  templateUrl: './carrier-form.component.html',
  styleUrls: ['./carrier-form.component.scss']
})
export class CarrierFormComponent implements OnInit, ComponentCanDeactivate {

  carrier: Carrier                = new Carrier(new FeatureProfile());
  regions: Region[]               = [];
  tmsLoadsSynchronizationRequired = false;
  mode                            = DisplayMode.Creation;
  DisplayMode                     = DisplayMode;
  TmsType                         = TmsType;
  tmsPickStartDate: string;

  formTitle: string;

  formType  = CarrierFormType.CREATE;
  FormTypes = CarrierFormType;

  formProcessingBlocked = false;

  blujayUrls: BlujayUrl[] = [];
  timezones: string[]     = [];
  languages: {value: string, label: string}[];

  modalBluechatCode               = null;
  selectedUsersToCreateInBluechat = [];

  config: IDatePickerDirectiveConfig = {
    appendTo: 'body',
    drops: 'up',
    firstDayOfWeek: 'mo',
    format: 'YYYY-MM-DD HH:mm',
    max: moment(),
    showTwentyFourHours: true,
  };

  loadRangeOptions: RadioOption[] = [
    {
      labelKey: 'user-dashboard.carriers.form.loads-range.all',
      value: LoadRangeDisplayCma.ALL
    },
    {
      labelKey: 'user-dashboard.carriers.form.loads-range.week',
      value: LoadRangeDisplayCma.ONE_WEEK_BEFORE_AND_AFTER
    },
    {
      labelKey: 'user-dashboard.carriers.form.loads-range.two-days',
      value: LoadRangeDisplayCma.YESTERDAY_TO_TOMORROW
    },
    {
      labelKey: 'user-dashboard.carriers.form.loads-range.today',
      value: LoadRangeDisplayCma.TODAY
    }
  ];

  canSeeApiFamilyOption = this.authService.canManageCarriers();
  apiFamilyOptions: Option[] = [
    { value: ApiFamily.REGULAR, labelKey: 'user-dashboard.carriers.form.third-party.regular' },
    { value: ApiFamily.THIRD_PARTY, labelKey: 'user-dashboard.carriers.form.third-party.third-party' },
  ];

  @ViewChild('blueChatTemplate')
  modalTemplate: TemplateRef<any>;

  @ViewChild('carrierForm')
  form: NgForm;

  @ViewChild('scacCode')
  scacCodeInput: ElementRef;

  isBluechatActivated: boolean;
  bluechatEnabled = false;
  multipleVendorFlags: boolean;

  formErrorMessageKey: string = null;

  constructor(
    private route: ActivatedRoute,
    private navigation: NavigationService,
    public location: Location,
    private carrierService: CarrierService,
    private regionService: RegionService,
    private blujayUrlService: BlujayUrlService,
    private blujayService: BlujayService,
    private timezoneService: TimezoneService,
    private loaderService: LoaderService,
    private toastr: ToastrService,
    private modalService: ModalService,
    private authService: AuthenticationService,
    private translate: TranslateService,
    private errorHandler: ErrorHandlerService,
    private carrierManagerService: CarrierManagerService,
    private bluechatService: BluechatService,
    private featureService: FeatureService
  ) {
    this.translate.get('user-dashboard.carriers.form.page-title.creation').subscribe(value => {
      if (this.mode === DisplayMode.Creation) {
        this.formTitle = value;
      }
    });

    this.featureService
        .getFeatureStatusObservable(FEATURES.CARRIER_MULTIPLE_VENDOR_NUMBERS)
        .subscribe(flag => this.multipleVendorFlags = flag);

    const languagesLabelObservable = Languages.values().reduce((acc, lang) =>
      ({...acc, [lang.cmblLangCode]: this.translate.get(`languages.${lang.labelKey}`)}), {});

    forkJoin(languagesLabelObservable)
      .pipe(
        map((languagesWithLabelObject: {}) => {
          let result = [];
          for (const locale of Object.keys(languagesWithLabelObject)) {
            result = [...result, {value: locale, label: languagesWithLabelObject[locale]}];
          }
          return result;
        })
      )
      .subscribe(languagesWithLabel => this.languages = languagesWithLabel);

    combineLatest([this.route.params, this.route.data])
      .subscribe(([{ carrierId }, { type }]) => {
        this.formType = type;

        let getCarrierObservable: Observable<Carrier>;
        let regionsObservable: Observable<Region[]> = of([]);

        switch (this.formType) {
          case CarrierFormType.CREATE:
            getCarrierObservable = of(new Carrier(new FeatureProfile()));
            regionsObservable    = regionService.getAllLight();
            break;
          case CarrierFormType.UPDATE:
            getCarrierObservable = carrierService.getById(carrierId)
              .pipe(
                tap(() => this.switchToUpdateMode()),
                catchError((error: HttpErrorResponse) => {
                  console.log(error);
                  this.toastr.error(this.translate.instant('user-dashboard.carriers.form.errors.fetch-carrier'));
                  this.navigation.carriers().list()
                    .go();

                  return of(new Carrier(new FeatureProfile()));
                }),
              );
            regionsObservable    = regionService.getAllLight();
            break;
          case CarrierFormType.UPDATE_OWN:
            getCarrierObservable = carrierService.getSelfCarrier()
              .pipe(
                tap(() => this.switchToUpdateMode()),
                catchError((error: HttpErrorResponse) => {
                  console.log(error);
                  this.toastr.error(this.translate.instant('user-dashboard.carriers.form.errors.fetch-own-carrier'));
                  this.navigation.selfCarrier().drivers().list()
                    .go();

                  return of(new Carrier(new FeatureProfile()));
                })
              );
            break;
        }

        const blujayUrlsObservable = blujayUrlService.getAll();
        const timezonesObservable  = timezoneService.getTimezones();

        this.blockForm(false);

        forkJoin([getCarrierObservable, blujayUrlsObservable, regionsObservable, timezonesObservable])
          .pipe(finalize(() => this.unblockForm()))
          .subscribe(([carrier, blujayUrls, regions, timezones]) => {
            this.carrier          = carrier;
            this.blujayUrls       = blujayUrls;
            this.regions          = regions;
            this.tmsPickStartDate = this.carrier.tmsLoadPickStartDate ? this.carrier.tmsLoadPickStartDate.format(this.config.format) : null;
            this.timezones           = timezones;
            this.isBluechatActivated = this.carrier.bluechatEnabled()
          });

      });
  }

  ngOnInit() {
    this.featureService.getFeatureStatusObservable(FEATURES.BLUECHAT).subscribe(bluechatStatus => this.bluechatEnabled = bluechatStatus);
  }

  getForm() {
    return this.form;
  }

  switchToUpdateMode(): void {
    this.mode = DisplayMode.Edition;
    this.translate.get('user-dashboard.carriers.form.page-title.edition').subscribe(value => this.formTitle = value);
  }

  updatePickStartDate(): void {
    const momentValue = moment(this.tmsPickStartDate, this.config.format);

    if (!this.tmsPickStartDate) {
      this.carrier.tmsLoadPickStartDate = null;
    } else if (momentValue.isValid()) {
      this.carrier.tmsLoadPickStartDate = momentValue;
    }

    this.form.form.markAsDirty();
  }

  processForm(): void {
    if (!this.checkScacCodeAndVendorNumber()) {
      return;
    }

    if (!this.checkPreferredLanguageNotEmpty()) {
      return;
    }

    this.formProcessingBlocked = true;
    if (this.mode === DisplayMode.Edition) {
      this.updateCarrier();
    } else {
      this.createCarrier();
    }
  }

  checkScacCodeAndVendorNumber(): boolean {
    if (this.formType === CarrierFormType.UPDATE_OWN) {
      return true;
    }

    const scacCodeControl = this.form.controls['carrier-scac-code'];
    const vendorNumberControl = this.multipleVendorFlags ? this.form.controls['carrier-vendor-numbers'] : this.form.controls['carrier-vendor-number'];

    if (!scacCodeControl.value && (!vendorNumberControl.value || vendorNumberControl.value.length === 0)) {
      scacCodeControl.setErrors({ scacCodeAndVendorEmpty: true });
      vendorNumberControl.setErrors({ scacCodeAndVendorEmpty: true });
      this.scacCodeInput.nativeElement.focus();
      return false;
    } else {
      scacCodeControl.setErrors({ scacCodeAndVendorEmpty: null });
      scacCodeControl.updateValueAndValidity();
      vendorNumberControl.setErrors({ scacCodeAndVendorEmpty: null });
      vendorNumberControl.updateValueAndValidity();
    }

    return true;
  }

  checkPreferredLanguageNotEmpty(): boolean {
    const preferredLanguageControl = this.form.controls['carrier-preferred-language'];

    if (!preferredLanguageControl.value) {
      preferredLanguageControl.setErrors({ preferredLanguageEmpty: true });
      return false;
    } else {
      preferredLanguageControl.setErrors({ preferredLanguageEmpty: null });
      preferredLanguageControl.updateValueAndValidity();
    }

    return true;
  }

  createCarrier(): void {
    this.loaderService.startLoad();

    this.carrierService.create(this.carrier)
      .pipe(finalize(() => {
        this.loaderService.stopLoad();
        this.formProcessingBlocked = false;
      }))
      .subscribe(carrier => {
        this.toastr.success(this.translate.instant('user-dashboard.carriers.form.successes.create'));

        this.navigation.carriers()
          .managers(carrier.id)
          .create()
          .go();
      }, e => this.errorHandler.handleHttpError(e, 'user-dashboard.carriers.form.errors.create'));
  }

  updateCarrier(): void {
    this.loaderService.startLoad();

    observableIif(() => this.formType === CarrierFormType.UPDATE_OWN,
      this.carrierService.updateSelfCarrier(this.carrier),
      this.carrierService.update(this.carrier)
    )
      .pipe(finalize(() => {
        this.loaderService.stopLoad();
        this.formProcessingBlocked = false;
      }))
      .subscribe(carrier => {
        this.form.form.markAsPristine();
        this.carrier = carrier;

        this.checkIfTmsLoadsSynchronizationRequired();

        this.toastr.success(this.translate.instant('user-dashboard.carriers.form.successes.update'));
      }, e => this.errorHandler.handleHttpError(e, 'user-dashboard.carriers.form.errors.update'));
  }

  toggleBlockingCarrier(): void {
    this.loaderService.startLoad();
    this.carrierService.toggleBlocking(this.carrier)
      .pipe(finalize(() => this.loaderService.stopLoad()))
      .subscribe(succeed => {
        if (succeed) {
          this.carrier.blocked = !this.carrier.blocked;
        }

        this.toastr.success(this.carrier.blocked ?
          this.translate.instant('user-dashboard.carriers.form.successes.block') :
          this.translate.instant('user-dashboard.carriers.form.successes.unblock')
        );
      }, (error: HttpErrorResponse) => {
        console.log(error);
        const defaultMessage = this.carrier.blocked ?
          this.translate.instant('user-dashboard.carriers.form.errors.unblock') :
          this.translate.instant('user-dashboard.carriers.form.errors.block');
        this.toastr.error(error.error.message || defaultMessage);
      });
  }

  canManageCarrierFeatureProfile(): boolean {
    return this.authService.canManageCarriers() && !!this.carrier.region;
  }

  changeTmsType() {
    if (this.carrier.tmsType !== TmsType.BLUJAY) {
      this.carrier.blujayUrl                  = null;
      this.carrier.blujayCredentials.username = null;
      this.carrier.blujayCredentials.apiToken = null;
    }

    if (this.carrier.tmsType !== TmsType.BLUEYONDER) {
      this.carrier.blueyonderCarrierCode = null;
    }

    this.checkIfTmsLoadsSynchronizationRequired();
  }

  checkIfTmsLoadsSynchronizationRequired() {
    if (!this.carrier.featureProfile.tmsLoadsSynchronization || this.carrier.tmsType !== TmsType.BLUJAY) {
      this.tmsLoadsSynchronizationRequired = false;
      return;
    }

    this.tmsLoadsSynchronizationRequired = !this.carrier.lastTmsSynchronization;
  }

  checkBlujayCredentials(event) {
    event.preventDefault();

    this.loaderService.startLoad();

    this.blujayService.verifyCredentials(
      this.carrier.blujayUrl,
      this.carrier.blujayCredentials.username,
      this.carrier.blujayCredentials.apiToken
    )
      .pipe(finalize(() => this.loaderService.stopLoad()))
      .subscribe((res) => {
        if (res) {
          this.toastr.success(this.translate.instant('user-dashboard.carriers.form.successes.verify-blujay-credentials'));
        } else {
          this.toastr.error(this.translate.instant('user-dashboard.carriers.form.errors.verify-blujay-credentials'));
        }
      }, e => this.errorHandler.handleHttpError(e, 'user-dashboard.carriers.form.errors.verify-blujay-unknown-error', false));
  }

  equalsRegionsId(r1: Region, r2: Region) {
    return r1 && r2 && r1.id === r2.id;
  }

  blockForm(smallLoader = true) {
    this.formProcessingBlocked = true;
    this.loaderService.startLoad(smallLoader);
  }

  unblockForm() {
    this.loaderService.stopLoad();
    this.formProcessingBlocked = false;
  }

  regionChanged() {
    this.regionService.getById(this.carrier.region.id)
      .subscribe((region: Region) => {
        this.carrier.region = region;
        this.carrier.featureProfile.applyRegionFeatures(this.carrier.region.features);
      });
  }

  openEnableBluechatModal() {
    this.loaderService.startLoad();
    this.carrierManagerService.getByCarrier(this.carrier.id)
      .subscribe( users => {
        this.loaderService.stopLoad()
        this.modalService.open({
          title: this.translate.instant('bluechat.modal.title'),
          size: 'sm',
          data: {
            users
          },
          content: this.modalTemplate
        }).subscribe( () => {
          this.enableBluechat();
        }, e => {
          this.errorHandler.handleHttpError(e, 'user-dashboard.carriers.form.errors.create')
        });
      });
  }


  enableBluechat() {
    this.loaderService.startLoad();

    this.bluechatService.enableBluechatOnCarrier(this.carrier.id, this.modalBluechatCode, this.selectedUsersToCreateInBluechat)
      .pipe(finalize(() => this.loaderService.stopLoad()))
      .subscribe( bluechatCompany => {
        this.toastr.success(this.translate.instant('user-dashboard.carriers.form.successes.enable-bluechat'));
        this.isBluechatActivated = true;
        this.carrier.bluechatCarrierCode = bluechatCompany.code;
        this.carrier.computeEligibleToBluechatActivation();
      }, e => this.errorHandler.handleHttpError(e, 'user-dashboard.carriers.form.errors.enable-bluechat'));
  }
}
